/**
 * Dev Drawer — visual drawer compact heading behaviour.
 *
 * For each .dev-drawer--visual element on the page:
 *  1. Reads the text of [data-dev-drawer-heading] and mirrors it (single line)
 *     into .dev-drawer__titlebar-label so CSS can truncate it.
 *  2. Watches .dev-drawer__body scroll. When the main heading's bottom edge
 *     exits the body's top edge, adds .dev-drawer--heading-scrolled to the
 *     component — this triggers the CSS fade-in of the compact title.
 *
 * Native theme drawers: observation runs while <dialog open> is set.
 * Legacy external drawers may use `.is-open` on the root instead of `<dialog open>`.
 */

const INIT_ATTR = 'data-dev-drawer-visual-init';
const SCROLL_ATTR = 'data-dev-drawer-scroll-bound';

/**
 * @param {Element} drawerEl
 */
export function initVisualDrawer(drawerEl) {
  const dialogEl = drawerEl.querySelector('dialog');
  const bodyEl = drawerEl.querySelector('.dev-drawer__body');
  const headingEl = drawerEl.querySelector('[data-dev-drawer-heading]');
  const labelEl = drawerEl.querySelector('.dev-drawer__titlebar-label');

  if (!(bodyEl instanceof HTMLElement) || !(headingEl instanceof HTMLElement) || !(labelEl instanceof HTMLElement)) {
    return;
  }

  const headingClone = headingEl.cloneNode(true);
  if (headingClone instanceof HTMLElement) {
    headingClone.querySelectorAll('br').forEach((br) => br.replaceWith(' '));
    labelEl.textContent = headingClone.textContent.trim().replace(/\s+/g, ' ');
  }

  function isOpen() {
    if (dialogEl instanceof HTMLDialogElement) {
      return dialogEl.hasAttribute('open');
    }

    return drawerEl.classList.contains('is-open');
  }

  function checkScroll() {
    const heading = drawerEl.querySelector('[data-dev-drawer-heading]');
    if (!(heading instanceof HTMLElement)) {
      drawerEl.classList.remove('dev-drawer--heading-scrolled');
      return;
    }

    const headingBottom = heading.getBoundingClientRect().bottom;
    const bodyTop = bodyEl.getBoundingClientRect().top;
    drawerEl.classList.toggle('dev-drawer--heading-scrolled', headingBottom <= bodyTop);
  }

  function startListening() {
    if (drawerEl.getAttribute(SCROLL_ATTR) === 'true') {
      checkScroll();
      return;
    }

    drawerEl.setAttribute(SCROLL_ATTR, 'true');
    drawerEl.classList.remove('dev-drawer--heading-scrolled');
    checkScroll();
    bodyEl.addEventListener('scroll', checkScroll, { passive: true });
  }

  function stopListening() {
    if (drawerEl.getAttribute(SCROLL_ATTR) !== 'true') {
      return;
    }

    drawerEl.removeAttribute(SCROLL_ATTR);
    bodyEl.removeEventListener('scroll', checkScroll);
    drawerEl.classList.remove('dev-drawer--heading-scrolled');
  }

  function handleOpenChange() {
    if (isOpen()) {
      requestAnimationFrame(startListening);
      return;
    }

    stopListening();
  }

  if (drawerEl.getAttribute(INIT_ATTR) !== 'true') {
    drawerEl.setAttribute(INIT_ATTR, 'true');

    if (dialogEl instanceof HTMLDialogElement) {
      new MutationObserver(handleOpenChange).observe(dialogEl, {
        attributes: true,
        attributeFilter: ['open'],
      });
    } else {
      new MutationObserver(handleOpenChange).observe(drawerEl, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  }

  if (isOpen()) {
    requestAnimationFrame(startListening);
  }
}

if (typeof window !== 'undefined') {
  window.__initDevVisualDrawer = initVisualDrawer;
}

/** @param {Element} node */
function scanForVisualDrawers(node) {
  if (!(node instanceof Element)) {
    return;
  }

  if (node.matches('.dev-drawer--visual')) {
    initVisualDrawer(node);
  }

  for (const drawer of node.querySelectorAll('.dev-drawer--visual')) {
    initVisualDrawer(drawer);
  }
}

document.querySelectorAll('.dev-drawer--visual').forEach(initVisualDrawer);

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      scanForVisualDrawers(node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });
